import { CtOrder, CtPacking, PackingItem } from '@/models/interfaces/content-type'
import { ProductSelection, ReplacementInstruction, OrderListProductState, ProductDragState, ProductView, ReplacementType } from '../interfaces'
import { ComputedRef, reactive } from 'vue'
import { debounce } from '@/utils'
import { createCanvas } from '@/utils/template'

export default function useProductOrdering (parsedPackages: ComputedRef<Array<PackingItem>>, packingOptions, productState, fetchPacking, emitPacking) {
  async function autoSplit () {
    const packIndex = parsedPackages.value.findIndex(p => p.products?.find(p => p.quantity > 1))
    if (parsedPackages.value[packIndex].products.some((p:any) => p.draft)) return

    if (packIndex < 0) return
    const component = parsedPackages.value[packIndex].components?.find(p => p.quantity > 1)
    if (component) await setCustomPacking(component, packIndex)
  }

  function computeProducts (components: PackingItem['components']) {
    return components.reduce<Array<{id: string, quantity: number}>>((products, comp) => {
      // look up the parent
      const index = products.findIndex(p => p.id === comp.parent.id)
      // const count = components.reduce((sum,c) => c.parent.id === comp.parent.id ? sum+1 : sum, 0)
      // add the component quantity
      if (index >= 0) products[index].quantity = products[index].quantity + ((comp.quantity || 0))
      else products.push({id: comp.parent.id, quantity: (comp.quantity || 0)})

      return products
    }, [])
  }

  async function setCustomPacking (add?: PackingItem['components'][number], splitAt?:number) {
    // console.log('setCustomPacking', add, splitAt)
    if (!packingOptions.value) await fetchPacking()
    // set the calculated packing as the updated packing value as a starting point for adding the new packing
    const packing = [...parsedPackages.value]

    let pack:PackingItem | undefined = undefined
    let componentIndex: number

    let splitQuantity = 0
    if (splitAt !== undefined) {
      if (add && typeof add !== 'object') {
        console.warn('Could not split product into separate packages as no product object was passed')
      } else {
        // halve the quantity of the product at it's current index
        // console.log(split, packing)
        pack = packing[splitAt]

        // if target has draft products, no-op
        if (pack?.products.some(s => (s as any).draft)) return

        componentIndex = pack.components.findIndex(p => p.id === add?.id)
        const startQuantity = pack.components[componentIndex].quantity

        if (startQuantity > 1) {
          const reduction = (Number.isInteger(pack.components[componentIndex].quantity / 2))
            ? pack.components[componentIndex].quantity / 2
            : (pack.components[componentIndex].quantity - 1) / 2

          // set new quantity
          pack.components[componentIndex].quantity = reduction
          splitQuantity = startQuantity - reduction

          // recompute packing array from components
          pack.products = computeProducts(pack.components)
        }
        // else move the item to the desination
        // else if (startQuantity === 1 && pack.products.length > 1) {
        //   pack.products[componentIndex].quantity = 0
        //   splitQuantity = 1
        // }

        // console.log('splitQuantity', startQuantity, componentIndex, pack.components[componentIndex]?.quantity, splitQuantity)
      }
    }
    if (add) {
      const productPacking = (splitAt !== undefined && splitQuantity > 0)
        ? pack
        : packingOptions.value[0]

      const components = (splitAt === undefined || splitQuantity <= 0)
      ? []
      : [{...packing[splitAt].components[packing[splitAt].components.findIndex(p => p.id === add?.id)],
          quantity: splitQuantity
        }]
      const addition = {
        ...productPacking,
        draft: true,
        weight: productPacking.weight || 1,
        dimensions: { x: productPacking.dimensions?.x || 1, y: productPacking.dimensions?.y || 1, z: productPacking.dimensions?.z || 1 },
        // recalculates product quantities based on updated component quantities,
        // each divided by the ammount of unique components in the package giving the "product" quantity
        products: computeProducts(components),
        components
      }

      packing.push(addition)
    }

    // HOFIX: explude packing with drafts (i.e added products) in emit
    emitPacking(packing.filter(f => !!f && !f.products?.some(s => (s as any).draft) && f.products?.length))
    productState.diff = true
  }

  function assignProductToTarget () {
    const {packIndex = -1, componentIndex = -1} = dragState.dragging || {}

    // NOTE will create packing from generated parsedPacking if it was not already defined
    const packing = [...parsedPackages.value]
    const currentPosition = packIndex
    const targetPosition = dragState.assignTarget ?? -1
    console.log(`drag from pack ${packIndex}, product ${componentIndex} to -> pack ${dragState.assignTarget}`)

    // HOTFIX: if the current item is a "draft" no-op
    if (packing[currentPosition].products.some((p:any) => p.draft)) return

    if (componentIndex < 0)
      return console.warn('Could not assign product to packing. no product id was defined as dragging')

    if (targetPosition < 0)
      return console.warn('Could not assign product to packing. target was undefined')

    if (currentPosition === targetPosition) return
    if (currentPosition < 0 || !packing[currentPosition])
      return console.error('Could not re-assign product. the current position index was undefined or had no products', currentPosition, packing)

    if (targetPosition < 0 || !packing[targetPosition])
      return console.warn('Could not re-assign product. the target position index was undefined')

    // HOTFIX: if the target is a "draft" no-op
    if (packing[targetPosition].products.some((p:any) => p.draft)) return
    // ---- paranoid safety checks all done, moving on...

    // fetch the pack item component at index, and splice if from components + splice the parent from products
    const [component] = packing[currentPosition].components.splice(componentIndex, 1)

    // the parent might be for multiple components so we cannot simply splice it out,
    // instead find the index, and check if the component quantity < the product quantity, and if so, only adjust the quantity
    const parentIndex = packing[currentPosition].products.findIndex(cp => cp.id === component.parent.id && cp.quantity)

    const parent = {...packing[currentPosition].products[parentIndex]}
    if (parent.quantity > component.quantity) {
      // exchange quantities
      packing[currentPosition].products[parentIndex].quantity = parent.quantity - component.quantity
      parent.quantity = component.quantity
    } else {
      packing[currentPosition].products.splice(parentIndex, 1)
    }

    packing[targetPosition].products.push({...parent})
    packing[targetPosition].products = packing[targetPosition].products.reduce<PackingItem['products']>((items, next) => {
      const index = items.findIndex(i => i.id === next.id)
      if (index < 0) items.push(next)
      else items.splice(index, 1, {...items[index], quantity: parseInt(items[index].quantity as any) + parseInt(next.quantity as any)})

      return items
    }, [])

    // assign the component to the target packing position, & reduce the new components array to merge components with matching id's (& parents - for now since this would cause issues with validation)
    packing[targetPosition].components.push(component)
    packing[targetPosition].components = packing[targetPosition].components.reduce<PackingItem['components']>((items, next) => {
      const index = items.findIndex(comp => comp.id === next.id)

      if (index < 0) items.push(next)
      else items.splice(index, 1, {...items[index], quantity: parseInt(items[index].quantity as any) + parseInt(next.quantity as any)})

      return items
    }, [])

    // if origin package is empty, remove it
    if (!packing[currentPosition].products.length && !packing[currentPosition].components.length) packing.splice(currentPosition, 1)

    emitPacking(packing)
    productState.diff = true
  }

  const createProductDragImg = (product) => {
    const fontSize = 12
    const padding = fontSize / 2
    const label = product?.sku || product?.title
    const width = (label?.length * fontSize) + (padding * 4)
    const height = (fontSize * 2) + (padding * 2)
    const { id, canvas, context, dpi } = createCanvas({ width, height })
    dragState.dragId = id

    context.fillStyle = '#ffffff'
    context.strokeStyle = '#7ea3ff'
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    context.roundRect(1, 1, width-2, (height-2) * .75, 20).fill()
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    context.roundRect(1, 1, width-2, (height-2) * .75, 20).stroke()


    context.fillStyle = '#0b4dff'
    context.strokeStyle = ''
    context.font = `bold ${fontSize}px Main`
    context.fillText(`${product.quantity}x  ${label}`, padding * 2, padding * 3)


    return canvas
  }

  function destroyProductDragImg () {
    if (!dragState.dragId) return
    const e = document.getElementById(dragState.dragId)
    if (!e) return
    document.body.removeChild(e)
  }

  const dragState = reactive<ProductDragState>({
    dragging: undefined,
    assignTarget: undefined,
    dragId: undefined
  })

  return {
    autoSplit,
    createProductDragImg,
    setCustomPacking,
    assignProductToTarget,
    destroyProductDragImg,
    dragState,
      drag: {
        start: (product, packIndex, componentIndex, e) => {
          dragState.dragging = { packIndex, componentIndex }

          e.dataTransfer.setData('text/plain', product.id)
          e.dataTransfer.dropEffect = 'move'
          e.dataTransfer.setDragImage(createProductDragImg(product), -30, 40)
        },
        end: async (product, index, e) => {
          if (dragState.handler) {
            await dragState.handler(product, index, e)
            dragState.handler = undefined
            dragState.dragging = undefined
            dragState.assignTarget = undefined
            // e.dataTransfer?.setDragImage(null, -30, 40)
            if (dragState.dragId) document.getElementById(dragState.dragId)?.remove()
            return
          }
          assignProductToTarget()
          dragState.dragging = undefined
          destroyProductDragImg()
          dragState.assignTarget = undefined
          // dragState.assignTarget = undefined
          // console.log('dragEnd', e)
        },
        over: debounce((pack, index, e) => {
          // console.log(pack.id, pack)
          if (!dragState.dragging) return
          dragState.assignTarget = index
          // console.log('dragOver', e)
        }, 200)
      }
  }
}
